/**
 * Single source of truth for membership tier display + pricing on the frontend.
 * Mirrors backend/core/membership.py.
 *
 * Checkout is handled by Creem: the Pricing page POSTs {tier, interval} to
 * /api/checkout and redirects the browser to the returned Creem-hosted
 * checkout URL. No provider URLs live in the frontend anymore.
 */
export const TIERS = [
  {
    id: "insider",
    name: "Insider",
    tagline: "Get inside the Vaymilo workshop.",
    priceMonthly: 3,
    priceAnnual: 32.4,
    accent: "#9CA3AF", // slate gray
    perks: [
      "Premium newsletter (members-only content)",
      "24-hour early access to free public resources",
      "Free AI Prompt Starter Kit on signup",
      "Vaymilo community access (Discord)",
      "Insider badge",
      "Direct replies on feedback",
    ],
  },
  {
    id: "studio",
    name: "Studio",
    tagline: "All-access. Every product. Every future drop.",
    priceMonthly: 12,
    priceAnnual: 129.6,
    accent: "#2563EB", // Vaymilo blue
    featured: true,
    perks: [
      "Everything in Insider",
      "Full access to the entire Vaymilo library",
      "Every future drop included automatically",
      "One new template or kit every month",
      "Members-only Notion vault",
      "50/day rate limit on the AI Prompt Playground",
      "Priority email support",
      "Studio member badge",
    ],
  },
  {
    id: "founder",
    name: "Founder",
    tagline: "Limited to 100. Source files. Direct line.",
    priceMonthly: 29,
    priceAnnual: 313.2,
    accent: "#111111", // charcoal
    capped: "Limited to 100 members",
    perks: [
      "Everything in Studio",
      "Editable source files (Figma, Notion masters, raw prompts)",
      "2-week early beta access to every drop",
      "Direct Discord channel with the Vaymilo team",
      "Vote on the product roadmap",
      "Quarterly Build Log — behind-the-scenes",
      "Lifetime archive of all drops during membership",
      "Founder badge + your name in product credits (opt-in)",
    ],
  },
];

export const getTier = (id) => TIERS.find((t) => t.id === id);

export const TIER_LABEL = {
  insider: "Insider",
  studio: "Studio",
  founder: "Founder",
};

/** POST /api/checkout and send the browser to the Creem-hosted checkout. */
export async function startCheckout(tierId, interval) {
  const res = await fetch(
    `${process.env.REACT_APP_BACKEND_URL}/api/checkout`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ tier: tierId, interval }),
    }
  );
  if (!res.ok) throw new Error("checkout_failed");
  const data = await res.json();
  window.location.href = data.checkout_url;
}

/** One-off kit purchase — POST /api/checkout {product: slug} → Creem checkout. */
export async function buyProduct(slug) {
  const res = await fetch(
    `${process.env.REACT_APP_BACKEND_URL}/api/checkout`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ product: slug }),
    }
  );
  if (!res.ok) throw new Error("checkout_failed");
  const data = await res.json();
  window.location.href = data.checkout_url;
}
